/**
 * As conversas com a DiagIA — a tela de auditoria.
 *
 * **Por que isto não é o "DiagIA · log de consultas" que já existe.** Aquele é
 * um log por *chamada* e enxerga uma fração do que aconteceu: lista 114 conversas
 * em 30 dias, enquanto a tabela de conversas tem **39.389** no mesmo período. Ele
 * responde "quanto demorou e o que deu erro"; esta responde "o que essa oficina
 * perguntou, e o que a IA respondeu".
 *
 * **Nove em cada dez conversas estão vazias.** Medido em 12/09/2026: de 568
 * conversas do dia, **513 têm só a saudação da IA** — o cliente abriu o painel, o
 * veículo entrou no contexto e ninguém digitou nada. Por isso a tela abre
 * filtrando as que têm pergunta de gente (3.605 das 39.389 no mês), e diz quantas
 * ficaram de fora em vez de escondê-las calada.
 *
 * **O período é sempre obrigatório** e a exportação leva o recorte inteiro, não a
 * página: uma linha por mensagem, pergunta e resposta na ordem.
 */

import { useQuery } from "@tanstack/react-query"
import { api, obter } from "./cliente"

export type ConversaDaIA = {
  conversaId: number
  clienteId: number
  clienteNome: string
  data: string
  veiculo: string
  marca: string
  motor: string
  assunto: string
  sistema: string
  mensagens: number
  perguntas: number
  erros: number
  primeiraPergunta: string
}

export type ClienteQueConversou = {
  clienteId: number
  clienteNome: string
  conversas: number
}

export type PaginaDeConversas = {
  dataInicio: string
  dataFim: string
  clienteId: number | null
  somenteComPergunta: boolean
  total: number
  pagina: number
  porPagina: number
  /** Todas as conversas do período, com pergunta ou sem — o denominador. */
  totalDeConversasNoPeriodo: number
  mensagens: number
  clientes: ClienteQueConversou[]
  itens: ConversaDaIA[]
}

export type MensagemDaIA = {
  id: number
  remetente: string
  texto: string
  data: string
  feedback: number | null
}

export type ConversaCompleta = {
  id: number
  clienteId: number
  veiculoCompleto: string | null
  sistemaNome: string | null
  marcaNome: string | null
  veiculoModelo: string | null
  motorNome: string | null
  assuntoNome: string | null
  assuntosSistemas: Record<string, string[]>
  data: string
  mensagens: MensagemDaIA[]
}

export type FiltroDeConversas = {
  dataInicio: string
  dataFim: string
  clienteId?: number
  somenteComPergunta: boolean
  pagina: number
  porPagina?: number
}

export function usarConversasDaIA(f: FiltroDeConversas, ligado: boolean) {
  return useQuery({
    queryKey: ["ia-conversas", f] as const,
    enabled: ligado,
    // Uma janela de 30 dias custa ~7 s: refazer sozinha ao voltar para a aba
    // seria castigo por navegar.
    staleTime: 5 * 60_000,
    queryFn: () => obter<PaginaDeConversas>("api/relatorios/ia/conversas", {
      dataInicio: f.dataInicio,
      dataFim: f.dataFim,
      clienteId: f.clienteId || undefined,
      somenteComPergunta: f.somenteComPergunta,
      pagina: f.pagina,
      porPagina: f.porPagina ?? 50,
    }),
  })
}

/**
 * A transcrição de uma conversa.
 *
 * **A rota pede o id da *conversa*, não o do log.** O `ia/log` devolve as duas
 * coisas em campos parecidos (`id` é do log, `conversaIAId` é da conversa) e
 * trocar um pelo outro traz a conversa errada sem dar erro nenhum.
 */
export function usarConversa(conversaId: number) {
  return useQuery({
    queryKey: ["ia-conversa", conversaId] as const,
    enabled: conversaId > 0,
    staleTime: Infinity,
    queryFn: () => obter<ConversaCompleta>(`api/relatorios/ia/log/${conversaId}/detalhe`, {}),
  })
}

/**
 * Baixa a planilha do recorte inteiro.
 *
 * Pelo cliente axios e não por um `<a href>`: o token vive em memória, não em
 * cookie, então o link sairia sem autenticação.
 */
export async function exportarConversas(f: Omit<FiltroDeConversas, "pagina" | "porPagina">) {
  const r = await api.get("api/relatorios/ia/conversas/exportar", {
    params: {
      dataInicio: f.dataInicio,
      dataFim: f.dataFim,
      clienteId: f.clienteId || undefined,
      somenteComPergunta: f.somenteComPergunta,
    },
    responseType: "blob",
    // O teto de conversas volta como 400 com ProblemDetails; sem isto a mensagem
    // chegaria como Blob e viraria "não foi possível falar com o servidor".
    validateStatus: () => true,
  })

  if (r.status >= 400) {
    const texto = await (r.data as Blob).text()
    try {
      const p = JSON.parse(texto) as { detail?: string; title?: string }
      throw new Error(p.detail || p.title || `Erro ${r.status}.`)
    } catch (e) {
      if (e instanceof Error && e.message) throw e
      throw new Error(`Erro ${r.status} ao exportar.`)
    }
  }

  const disp = String(r.headers?.["content-disposition"] ?? "")
  const achado = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp)
  const nome = achado ? decodeURIComponent(achado[1]!) : "conversas-diagia.xlsx"

  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)

  return { nome, bytes: (r.data as Blob).size }
}
